import { useNavigate } from 'react-router'

import { useEventCapture } from '@surglogs-app/analytics'
import { useAuthToken } from '@surglogs-app/auth'
import { useHandler } from '@surglogs-app/hooks'
import {
  checkErrorResponseStatus,
  useAccessManagementAccountPinPostMutation,
  useAuthTokenRevokePostMutation,
} from '@surglogs-app/network'
import { getAuthLogoutRoute } from '@surglogs-app/routing'
import { Config } from '@surglogs-app/types'

import { useAccountPasswordUpdateFailureAlert } from './useAccountPasswordUpdateFailureAlert'
import { useReCaptchaToken } from './useReCaptchaToken'

export const useAccountEditPinSubmitHandler = ({
  hideModal,
  config,
}: {
  hideModal: () => void
  config: Pick<Config, 'reCaptchaKey'>
}) => {
  const [getReCaptchaToken] = useReCaptchaToken({ config })
  const { token, refreshToken, clearAuthData } = useAuthToken()
  const mutation = useAccessManagementAccountPinPostMutation(
    {},
    {
      opts: {
        throwOnError: false,
      },
    },
  )
  const { captureEvent } = useEventCapture()
  const navigate = useNavigate()

  const { mutateAsync: revokeJwtTokenMutateAsync } =
    useAuthTokenRevokePostMutation()

  const { showAccountPasswordUpdateFailureAlert } =
    useAccountPasswordUpdateFailureAlert()

  const onSubmit = useHandler(async (values) => {
    try {
      const reCaptchaToken = await getReCaptchaToken()
      await mutation.mutateAsync({
        data: {
          oldpassword: values.password,
          pin: values.pin,
          ...(reCaptchaToken != null ? { captcha_token: reCaptchaToken } : {}),
        },
      })

      hideModal()

      await captureEvent('account_pin_changed')

      navigate(getAuthLogoutRoute('pin_changed'))
    } catch (error) {
      if (checkErrorResponseStatus(error, 429)) {
        clearAuthData()

        if (token) {
          await revokeJwtTokenMutateAsync({
            refresh_token: refreshToken,
            access_token: token,
          })
        }
      }

      showAccountPasswordUpdateFailureAlert({ error })
    }
  })

  return [onSubmit, mutation] as const
}
